import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Truck, Power, MapPin, Clock, DollarSign, CheckCircle2, ArrowRight,
  Camera, Navigation, Wallet, FileText, User, LogOut, RefreshCw,
  ChevronRight, AlertCircle, Package, Loader2, Upload, CreditCard,
  Phone, MessageSquare, Edit3, Trash2, Lock, ExternalLink, Send, Image as ImageIcon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import PasswordChangeDialog from "@/components/PasswordChangeDialog";
import ReturnLoadsCarousel from "@/components/driver/ReturnLoadsCarousel";
import PerformanceWidget from "@/components/driver/PerformanceWidget";
import PendingUploadsBadge from "@/components/driver/PendingUploadsBadge";
import { platform as mobilePlatform, gps as mobileGps, push as mobilePush } from "@/mobile";
import { logDiag, tokenFingerprint } from "@/debug/diagJournal"; // TEMP-DIAG

const API = process.env.REACT_APP_BACKEND_URL;

// ============ UTILS ============
function getToken() { return localStorage.getItem("driver_token"); }
function authHeaders() { return { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" }; }

// Use axios instead of fetch + res.json() — the latter throws "Body is disturbed
// or locked" inside the Capacitor iOS WebView because CapacitorHttp consumes
// the response body when bridging to native URLSession. axios uses XHR, which
// CapacitorHttp handles correctly. (Same fix pattern as CustomerAuthPage.)
async function api(path, opts = {}) {
  const method = (opts.method || "GET").toUpperCase();
  const config = {
    method,
    url: `${API}${path}`,
    headers: { ...authHeaders(), ...(opts.headers || {}) },
    validateStatus: () => true, // don't throw on non-2xx; we inspect status manually
  };
  // axios uses `data` for request body
  if (opts.body !== undefined) {
    config.data = typeof opts.body === "string" ? JSON.parse(opts.body) : opts.body;
  }
  // TEMP-DIAG: record dispatch order + whether an Authorization header is being sent
  logDiag("api:start", { method, path, auth: tokenFingerprint(getToken()) });
  let res;
  try {
    res = await axios(config);
  } catch (e) {
    logDiag("api:network-error", { method, path, msg: String(e?.message || e).slice(0, 120) }); // TEMP-DIAG
    return { __error: true, status: 0, detail: "Network error" };
  }
  // TEMP-DIAG: record resolution order + status; capture body detail on 401
  logDiag("api:response", {
    method,
    path,
    status: res.status,
    ...(res.status === 401
      ? { body401: JSON.stringify(res.data).slice(0, 200), dataType: typeof res.data }
      : {}),
  });
  if (res.status === 401) {
    logDiag("api:401-TOKEN-WIPED-FULL-RELOAD", { path }); // TEMP-DIAG
    localStorage.removeItem("driver_token");
    window.location.href = "/driver/login";
    return null;
  }
  const data = res.data;
  if (res.status < 200 || res.status >= 300) {
    const detail = (data && (data.detail || data.message)) || `Request failed (${res.status})`;
    return { __error: true, status: res.status, detail };
  }
  return data || {};
}

function fileUrl(path) {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  return `${API}/api/files/${path}`;
}

function Avatar({ src, name, size = 36 }) {
  const initials = (name || "?").split(" ").map(s => s[0]).join("").slice(0, 2).toUpperCase();
  if (src) {
    return (
      <img
        src={src}
        alt={name || "avatar"}
        style={{ width: size, height: size }}
        className="rounded-full object-cover bg-slate-100 border border-slate-200"
        data-testid="driver-avatar-img"
      />
    );
  }
  return (
    <div
      style={{ width: size, height: size }}
      className="rounded-full bg-orange-100 text-orange-600 font-semibold flex items-center justify-center"
      data-testid="driver-avatar-initials"
    >
      <span style={{ fontSize: size * 0.4 }}>{initials}</span>
    </div>
  );
}

const STATUS_FLOW = [
  { key: "assigned", label: "Accepted", color: "bg-blue-500" },
  { key: "en_route_to_pickup", label: "En Route", color: "bg-indigo-500" },
  { key: "arrived", label: "Arrived", color: "bg-purple-500" },
  { key: "loaded", label: "Loaded", color: "bg-amber-500" },
  { key: "in_transit", label: "In Transit", color: "bg-orange-500" },
  { key: "delivered", label: "Delivered", color: "bg-emerald-500" },
  { key: "completed", label: "Completed", color: "bg-green-600" },
];

const NEXT_ACTION = {
  assigned: { endpoint: "start", label: "En Route to Pickup", icon: Navigation },
  en_route_to_pickup: { endpoint: "arrived", label: "Arrived at Pickup", icon: MapPin },
  arrived: { endpoint: "loaded", label: "Goods Loaded", icon: Package },
  loaded: { endpoint: "in-transit", label: "Start Delivery", icon: Truck },
  in_transit: { endpoint: "delivered", label: "Mark Delivered", icon: CheckCircle2 },
  delivered: { endpoint: null, label: "Submit POD to Complete", icon: Camera },
};

// ============ COMPONENTS ============

function OnlineToggle({ online, onToggle }) {
  return (
    <button
      onClick={onToggle}
      className={`relative w-16 h-8 rounded-full transition-colors ${online ? "bg-green-500" : "bg-slate-300"}`}
      data-testid="online-toggle"
    >
      <span className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow transition-transform ${online ? "left-9" : "left-1"}`} />
    </button>
  );
}

const DISPATCH_STATUS_OPTIONS = [
  { key: "available", label: "Available", color: "bg-green-500", desc: "Top priority for new jobs" },
  { key: "returning_empty", label: "Returning Empty", color: "bg-cyan-500", desc: "Open to return-load jobs" },
  { key: "online", label: "Online", color: "bg-blue-500", desc: "Visible but lower priority" },
  { key: "scheduled", label: "Scheduled", color: "bg-purple-500", desc: "On a planned route" },
  { key: "busy", label: "Busy", color: "bg-amber-500", desc: "Not receiving new jobs" },
  { key: "offline", label: "Offline", color: "bg-slate-400", desc: "Not visible" },
];

function DispatchStatusBadge({ status, online }) {
  const opt = DISPATCH_STATUS_OPTIONS.find(o => o.key === status) ||
              (online ? DISPATCH_STATUS_OPTIONS[0] : DISPATCH_STATUS_OPTIONS[5]);
  return (
    <p className="text-xs text-slate-500 flex items-center gap-1" data-testid="dispatch-status-badge">
      <span className={`w-1.5 h-1.5 rounded-full ${opt.color}`} />
      {opt.label}
    </p>
  );
}

function DispatchStatusSelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const current = DISPATCH_STATUS_OPTIONS.find(o => o.key === value) || DISPATCH_STATUS_OPTIONS[0];
  return (
    <div className="relative" data-testid="dispatch-status-select">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-white ${current.color} active:scale-95 transition`}
        data-testid="dispatch-status-trigger"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-white" />
        {current.label}
        <ChevronRight className={`w-3 h-3 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-30 bg-white rounded-xl shadow-xl border border-slate-200 w-56 overflow-hidden">
            {DISPATCH_STATUS_OPTIONS.map(opt => (
              <button
                key={opt.key}
                onClick={() => { onChange(opt.key); setOpen(false); }}
                className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-start gap-2 ${opt.key === value ? "bg-slate-50" : ""}`}
                data-testid={`dispatch-status-${opt.key}`}
              >
                <span className={`w-2 h-2 rounded-full ${opt.color} mt-1.5 flex-shrink-0`} />
                <span className="flex-1">
                  <span className="text-sm font-medium text-slate-900 block">{opt.label}</span>
                  <span className="text-[11px] text-slate-500">{opt.desc}</span>
                </span>
                {opt.key === value && <span className="text-orange-500 text-sm">•</span>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color = "text-slate-900" }) {
  return (
    <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 text-slate-400" />
        <span className="text-xs text-slate-500 font-medium">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function JobCard({ job, onSelect, accepted = false }) {
  const status = STATUS_FLOW.find(s => s.key === job.status);
  // Drivers ONLY ever see their 75% payout, never the gross customer price
  const payout = job.driver_payout ?? 0;
  // Pre-accept: only show outward postcode (e.g. "SW1A"). Post-accept: full postcode
  const pickup = accepted
    ? (job.pickup_postcode || job.pickup_area || "Pickup")
    : (job.pickup_area || "Pickup area");
  const delivery = accepted
    ? (job.delivery_postcode || job.delivery_area || "Delivery")
    : (job.delivery_area || "Delivery area");

  return (
    <div
      onClick={() => onSelect && onSelect(job)}
      className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md active:scale-[0.98] transition-all cursor-pointer"
      data-testid={`job-card-${job.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${status?.color || "bg-slate-500"} text-white`}>
            {status?.label || job.status}
          </span>
          {!accepted && job.dispatch_tier === "priority" && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700 animate-pulse border border-red-300" title="Priority — closest drivers only">
              ⚡ PRIORITY
            </span>
          )}
          {!accepted && job.dispatch_tier === "regional" && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-300" title="Regional — wider radius">
              REGIONAL
            </span>
          )}
          <span className="text-[10px] font-mono text-slate-400">#{job.id.slice(0, 6).toUpperCase()}</span>
        </div>
        <div className="text-right">
          <p className="text-[10px] text-slate-400 font-medium">YOU EARN</p>
          <span className="text-lg font-bold text-green-600" data-testid={`job-payout-${job.id.slice(0, 6)}`}>
            &pound;{payout.toFixed(2)}
          </span>
        </div>
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex items-start gap-2">
          <div className="w-2 h-2 rounded-full bg-green-500 mt-1.5 flex-shrink-0" />
          <span className="text-slate-700 font-medium">{pickup}</span>
          {!accepted && <Lock className="w-3 h-3 text-slate-300 ml-auto" title="Full address unlocks after accept" />}
        </div>
        <div className="flex items-start gap-2">
          <div className="w-2 h-2 rounded-full bg-red-500 mt-1.5 flex-shrink-0" />
          <span className="text-slate-700 font-medium">{delivery}</span>
        </div>
      </div>

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
        <span>
          {job.distance_to_pickup_miles !== undefined && job.distance_to_pickup_miles !== null && (
            <span className="font-semibold text-slate-700" data-testid={`job-pickup-distance-${job.id.slice(0, 6)}`}>
              {job.distance_to_pickup_miles}mi away ·
            </span>
          )}
          {job.distance_miles ? ` ${job.distance_miles} mi job` : ""}
        </span>
        <span className="capitalize">{job.item_type || ""} · {job.size || ""}</span>
        <ChevronRight className="w-4 h-4" />
      </div>

      {!accepted && job.notes_summary && (
        <p className="mt-2 text-[11px] text-slate-500 italic line-clamp-2">&ldquo;{job.notes_summary}&rdquo;</p>
      )}
    </div>
  );
}

function ActiveJobView({ job, onUpdate, loading }) {
  const currentIdx = STATUS_FLOW.findIndex(s => s.key === job.status);
  const action = NEXT_ACTION[job.status];
  const [chatMsgs, setChatMsgs] = useState([]);
  const [chatText, setChatText] = useState("");
  const [chatSending, setChatSending] = useState(false);
  const chatScrollRef = useRef(null);

  const fetchChat = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/chat/${job.id}/messages`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        validateStatus: () => true,
      });
      if (res.status < 200 || res.status >= 300) return;
      setChatMsgs(res.data?.messages || []);
    } catch {}
  }, [job.id]);

  useEffect(() => {
    fetchChat();
    const iv = setInterval(fetchChat, 3000);
    return () => clearInterval(iv);
  }, [fetchChat]);

  useEffect(() => {
    if (chatScrollRef.current) chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
  }, [chatMsgs]);

  const sendChat = async (e) => {
    e?.preventDefault();
    const t = chatText.trim();
    if (!t) return;
    setChatSending(true);
    try {
      const res = await axios.post(
        `${API}/api/chat/${job.id}/messages`,
        { message: t },
        {
          headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" },
          validateStatus: () => true,
        }
      );
      if (res.status < 200 || res.status >= 300) {
        toast.error(res.data?.detail || "Could not send");
        return;
      }
      setChatText("");
      fetchChat();
    } finally {
      setChatSending(false);
    }
  };

  const uploadChatImage = async (file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) { toast.error("Image too large (max 6MB)"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const up = await axios.post(`${API}/api/chat/${job.id}/upload`, fd, {
        headers: { Authorization: `Bearer ${getToken()}` },
        validateStatus: () => true,
      });
      if (up.status < 200 || up.status >= 300) {
        toast.error(up.data?.detail || "Upload failed");
        return;
      }
      await axios.post(
        `${API}/api/chat/${job.id}/messages`,
        { image_url: up.data.image_url },
        {
          headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" },
          validateStatus: () => true,
        }
      );
      fetchChat();
    } catch {
      toast.error("Upload failed");
    }
  };

  const navTo = (addr, postcode) => {
    const q = encodeURIComponent([addr, postcode].filter(Boolean).join(", "));
    // Apple Maps on iOS handles this URL; Android opens Google Maps app
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${q}`, "_blank", "noopener");
  };

  return (
    <div className="space-y-4" data-testid="active-job-view">
      {/* Header card with ref + payout */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white rounded-xl p-4 shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] text-slate-400 uppercase tracking-wide">Booking Ref</p>
            <p className="font-mono font-bold text-lg">#{job.id.slice(0, 8).toUpperCase()}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-slate-400 uppercase tracking-wide">Your Payout</p>
            <p className="text-2xl font-bold text-orange-400">&pound;{(job.driver_payout || 0).toFixed(2)}</p>
          </div>
        </div>
      </div>

      {/* Status Progress */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Job Progress</h3>
        <div className="flex items-center gap-1">
          {STATUS_FLOW.slice(0, -1).map((step, i) => (
            <div key={step.key} className="flex-1">
              <div className={`h-2 rounded-full ${i <= currentIdx ? step.color : "bg-slate-200"}`} />
            </div>
          ))}
        </div>
        <p className="text-center mt-2 text-sm font-medium text-slate-700">
          {STATUS_FLOW[currentIdx]?.label || job.status}
        </p>
      </div>

      {/* Customer Contact */}
      {(job.customer_name || job.customer_phone) && (
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="customer-contact-card">
          <p className="text-xs text-slate-500 mb-2">Customer</p>
          <p className="font-semibold text-slate-900 mb-3">{job.customer_name || "Customer"}</p>
          {job.customer_phone && (
            <div className="grid grid-cols-2 gap-2">
              <a
                href={`tel:${job.customer_phone}`}
                className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-medium text-sm active:scale-[0.97] transition-all"
                data-testid="call-customer-btn"
              >
                <Phone className="w-4 h-4" /> Call
              </a>
              <a
                href={`sms:${job.customer_phone}`}
                className="flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 py-3 rounded-lg font-medium text-sm active:scale-[0.97] transition-all"
                data-testid="sms-customer-btn"
              >
                <MessageSquare className="w-4 h-4" /> SMS
              </a>
            </div>
          )}
        </div>
      )}

      {/* In-app Chat with customer */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col" style={{ height: 360 }} data-testid="driver-chat-panel">
        <div className="bg-slate-900 text-white px-3 py-2 flex items-center gap-2">
          <MessageSquare className="w-3.5 h-3.5 text-orange-400" />
          <p className="text-xs font-semibold">Chat with customer</p>
        </div>
        <div ref={chatScrollRef} className="flex-1 overflow-y-auto bg-slate-50 p-2 space-y-1.5">
          {chatMsgs.length === 0 && (
            <div className="text-center text-[11px] text-slate-400 mt-6">No messages yet.</div>
          )}
          {chatMsgs.map(m => {
            const mine = m.sender_role === "driver";
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] rounded-2xl px-3 py-1.5 text-xs ${
                  mine ? "bg-orange-500 text-white rounded-br-sm" : "bg-white text-slate-800 border border-slate-200 rounded-bl-sm"
                }`}>
                  {m.image_url && (
                    <img src={fileUrl(m.image_url)} alt="" className="rounded-lg mb-1 max-w-full" />
                  )}
                  {m.message && <p className="whitespace-pre-wrap break-words">{m.message}</p>}
                  <p className={`text-[9px] mt-0.5 ${mine ? "text-orange-100" : "text-slate-400"}`}>
                    {new Date(m.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
        <form onSubmit={sendChat} className="p-2 border-t border-slate-100 flex items-center gap-1 bg-white">
          <label className="p-1.5 text-slate-500 hover:text-orange-500 cursor-pointer">
            <ImageIcon className="w-4 h-4" />
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
              className="hidden"
              onChange={(e) => { uploadChatImage(e.target.files[0]); e.target.value = ""; }}
            />
          </label>
          <input
            type="text"
            value={chatText}
            onChange={(e) => setChatText(e.target.value)}
            placeholder="Reply..."
            className="flex-1 text-xs border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:border-orange-400"
            data-testid="driver-chat-input"
          />
          <button
            type="submit"
            disabled={chatSending || !chatText.trim()}
            className="w-8 h-8 rounded-md bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white flex items-center justify-center"
            data-testid="driver-chat-send-btn"
          >
            <Send className="w-3.5 h-3.5" />
          </button>
        </form>
      </div>

      {/* Pickup */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
            <div className="w-3 h-3 rounded-full bg-green-500" />
          </div>
          <div className="flex-1">
            <p className="text-xs text-slate-500">Pickup</p>
            <p className="font-semibold text-slate-900">{job.pickup_address || "—"}</p>
            <p className="text-sm text-slate-500">{job.pickup_postcode}</p>
          </div>
        </div>
        <Button
          onClick={() => navTo(job.pickup_address, job.pickup_postcode)}
          variant="outline"
          className="w-full text-sm py-2 h-9 border-green-200 text-green-700 hover:bg-green-50"
          data-testid="navigate-pickup-btn"
        >
          <Navigation className="w-4 h-4 mr-2" /> Navigate to Pickup
        </Button>
      </div>

      {/* Delivery */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <div className="w-3 h-3 rounded-full bg-red-500" />
          </div>
          <div className="flex-1">
            <p className="text-xs text-slate-500">Delivery</p>
            <p className="font-semibold text-slate-900">{job.delivery_address || "—"}</p>
            <p className="text-sm text-slate-500">{job.delivery_postcode}</p>
          </div>
        </div>
        <Button
          onClick={() => navTo(job.delivery_address, job.delivery_postcode)}
          variant="outline"
          className="w-full text-sm py-2 h-9 border-red-200 text-red-700 hover:bg-red-50"
          data-testid="navigate-delivery-btn"
        >
          <Navigation className="w-4 h-4 mr-2" /> Navigate to Delivery
        </Button>
      </div>

      {/* Job specs */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="grid grid-cols-3 gap-3 text-center">
          <div>
            <p className="text-xs text-slate-500">Distance</p>
            <p className="font-semibold text-slate-900">{job.distance_miles} mi</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Item</p>
            <p className="font-semibold text-slate-900 capitalize">{job.item_type}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Size</p>
            <p className="font-semibold text-slate-900 capitalize">{job.size}</p>
          </div>
        </div>
        {job.notes && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <p className="text-xs text-slate-500 mb-1">Notes</p>
            <p className="text-sm text-slate-700">{job.notes}</p>
          </div>
        )}
      </div>

      {/* Action Button */}
      {action && action.endpoint && (
        <Button
          onClick={() => onUpdate(job.id, action.endpoint)}
          disabled={loading}
          className="w-full py-6 text-base font-semibold bg-orange-500 hover:bg-orange-600 text-white rounded-xl shadow-lg active:scale-[0.97] transition-all"
          data-testid="job-action-btn"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <action.icon className="w-5 h-5 mr-2" />}
          {action.label}
        </Button>
      )}

      {/* POD Section - when delivered */}
      {job.status === "delivered" && (
        <div className="bg-red-50 border-2 border-red-300 rounded-xl p-5 text-center animate-pulse">
          <Camera className="w-10 h-10 text-red-600 mx-auto mb-2" />
          <p className="font-bold text-red-900 text-lg">⚠️ Final step required</p>
          <p className="text-sm text-red-700 mt-1 font-medium">Upload a Proof-of-Delivery photo to release your payment.</p>
          <p className="text-xs text-red-600 mt-2">Until you complete this step, you will NOT be paid for this job.</p>
        </div>
      )}
    </div>
  );
}

function PODUploadView({ job, onComplete, loading }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [notes, setNotes] = useState("");
  const [gps, setGps] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {}
      );
    }
  }, []);

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (f) {
      setFile(f);
      setPreview(URL.createObjectURL(f));
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(
        `${API}/api/driver/jobs/${job.id}/pod/upload`,
        formData,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
          validateStatus: () => true,
        }
      );
      if (res.status >= 200 && res.status < 300) {
        setUploaded(true);
        // Submit details
        const params = new URLSearchParams();
        if (notes) params.append("notes", notes);
        if (gps) { params.append("gps_lat", gps.lat); params.append("gps_lng", gps.lng); }
        await axios.post(
          `${API}/api/driver/jobs/${job.id}/pod/details?${params}`,
          null,
          { headers: authHeaders(), validateStatus: () => true }
        );
      } else {
        throw new Error(`upload_${res.status}`);
      }
    } catch (e) {
      // Phase 4 — queue for offline retry if the upload failed.
      try {
        const { enqueue } = await import("@/pwa/podOfflineQueue");
        await enqueue({
          bookingId: job.id,
          file,
          authToken: getToken(),
          notes: notes || null,
          gps: gps || null,
        });
        toast.info("Saved for upload", {
          description: "Network unavailable — we'll send the POD automatically when you reconnect.",
        });
        setUploaded(true); // unblock the workflow even though it's queued
      } catch (qErr) {
        console.error(qErr);
        toast.error("Upload failed", { description: e.message || "Please try again." });
      }
    }
    setUploading(false);
  };

  const handleComplete = async () => {
    await onComplete(job.id);
  };

  return (
    <div className="space-y-4" data-testid="pod-upload-view">
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <Camera className="w-5 h-5 text-orange-500" /> Proof of Delivery
        </h3>
        
        {/* Photo Upload */}
        <div
          onClick={() => fileRef.current?.click()}
          className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center cursor-pointer hover:border-orange-400 transition-colors"
        >
          {preview ? (
            <img src={preview} alt="POD" className="w-full h-48 object-cover rounded-lg" />
          ) : (
            <>
              <Upload className="w-10 h-10 text-slate-400 mx-auto mb-2" />
              <p className="text-sm text-slate-600">Tap to take photo or upload</p>
            </>
          )}
        </div>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={handleFile} className="hidden" />
        
        {/* Notes */}
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Delivery notes (optional)"
          className="mt-3 w-full border border-slate-200 rounded-lg p-3 text-sm resize-none h-20 focus:ring-2 focus:ring-orange-200 focus:border-orange-400 outline-none"
          data-testid="pod-notes"
        />
        
        {/* GPS */}
        <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
          <Navigation className="w-3 h-3" />
          {gps ? `GPS: ${gps.lat.toFixed(4)}, ${gps.lng.toFixed(4)}` : "Acquiring GPS..."}
        </div>
      </div>

      {/* Upload then Complete */}
      {!uploaded ? (
        <Button
          onClick={handleUpload}
          disabled={!file || uploading}
          className="w-full py-6 text-base font-semibold bg-orange-500 hover:bg-orange-600 text-white rounded-xl shadow-lg"
          data-testid="pod-upload-btn"
        >
          {uploading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Upload className="w-5 h-5 mr-2" />}
          Upload Photo
        </Button>
      ) : (
        <Button
          onClick={handleComplete}
          disabled={loading}
          className="w-full py-6 text-base font-semibold bg-green-600 hover:bg-green-700 text-white rounded-xl shadow-lg"
          data-testid="pod-complete-btn"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}
          Complete Job
        </Button>
      )}
    </div>
  );
}

// ============ MAIN DASHBOARD ============

export default function DriverDashboardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState("home");
  const [profile, setProfile] = useState(null);
  const [activeJobs, setActiveJobs] = useState([]);
  const [availableJobs, setAvailableJobs] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showPOD, setShowPOD] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAll = useCallback(async () => {
    setRefreshing(true);
    const [p, active, available, w] = await Promise.all([
      api("/api/driver/me"),
      api("/api/driver/jobs/active"),
      api("/api/driver/jobs/available"),
      api("/api/driver/wallet"),
    ]);
    if (p && !p.__error) setProfile(p);
    if (active && !active.__error) setActiveJobs(active.jobs || []);
    if (available && !available.__error) setAvailableJobs(available.jobs || []);
    if (w && !w.__error) setWallet(w);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    if (!getToken()) { navigate("/driver/login"); return; }
    fetchAll();
    const interval = setInterval(fetchAll, 30000);
    return () => clearInterval(interval);
  }, [fetchAll, navigate]);

  // GPS location tracking - ping every 30s when online
  useEffect(() => {
    if (!profile?.online) return;
    let stopFn = null;
    let cancelled = false;

    (async () => {
      const isNative = await mobilePlatform.isNative();
      if (isNative) {
        // Phase 3a — native background-GPS adapter (plugin auto-falls back to
        // foreground watchPosition if the community plugin isn't installed).
        try {
          stopFn = await mobileGps.start({
            token: getToken(),
            apiBase: API,
            intervalMs: 30000,
          });
        } catch (e) { /* ignore */ }
        return;
      }
      // Web: existing foreground polling, unchanged behaviour.
      if (!navigator.geolocation) return;
      const pingLocation = () => {
        navigator.geolocation.getCurrentPosition(
          async (pos) => {
            const { latitude, longitude } = pos.coords;
            try {
              await axios.post(
                `${API}/api/driver/location?lat=${latitude}&lng=${longitude}`,
                null,
                {
                  headers: { Authorization: `Bearer ${getToken()}` },
                  validateStatus: () => true,
                }
              );
            } catch (e) { /* ignore network errors */ }
          },
          () => {},
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
        );
      };
      pingLocation();
      const locInterval = setInterval(pingLocation, 30000);
      stopFn = () => clearInterval(locInterval);
    })();

    return () => {
      if (cancelled) return;
      cancelled = true;
      if (typeof stopFn === "function") {
        try { stopFn(); } catch { /* ignore */ }
      } else if (stopFn && typeof stopFn.then === "function") {
        stopFn.then((fn) => { try { fn?.(); } catch { /* ignore */ } });
      }
    };
  }, [profile?.online]);

  // Phase 3b — Push notification registration (no-op on web; native bundles
  // register the device with APNs/FCM via the existing /push/subscribe endpoint).
  useEffect(() => {
    if (!profile?.approved) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await mobilePush.register({ token: getToken(), apiBase: API });
        if (!cancelled && result?.registered) {
          console.log("[push] device registered for notifications");
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [profile?.approved]);

  const downloadEarningsCSV = async () => {
    try {
      const res = await axios.get(`${API}/api/driver/earnings/csv`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        responseType: "blob",
        validateStatus: () => true,
      });
      if (res.status < 200 || res.status >= 300) return;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `earnings_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
  };

  const toggleOnline = async () => {
    const newState = !profile?.online;
    await api("/api/driver/online-status", { method: "POST", body: JSON.stringify({ online: newState }) });
    setProfile(prev => ({ ...prev, online: newState }));
  };

  const setDispatchStatus = async (newStatus) => {
    const res = await api("/api/driver/status", { method: "POST", body: JSON.stringify({ status: newStatus }) });
    if (res?.__error) {
      toast.error(res.detail || "Failed to update status");
      return;
    }
    setProfile(prev => ({ ...prev, status: newStatus, online: newStatus !== "offline" }));
    toast.success(`Status: ${newStatus.replace("_", " ")}`);
    fetchAll();
  };

  const acceptJob = async (bookingId) => {
    setLoading(true);
    const res = await api(`/api/driver/jobs/${bookingId}/accept`, { method: "POST" });
    if (res?.__error) {
      toast.error(res.detail || "Failed to accept job");
      setLoading(false);
      return;
    }
    toast.success("Job accepted");
    await fetchAll();
    setTab("active");
    setLoading(false);
  };

  const updateJobStatus = async (bookingId, action) => {
    setLoading(true);
    const res = await api(`/api/driver/jobs/${bookingId}/${action}`, { method: "POST" });
    if (res?.__error) {
      toast.error(res.detail || "Failed to update status");
      setLoading(false);
      return;
    }
    await fetchAll();
    const data = await api("/api/driver/jobs/active");
    const updated = data && !data.__error ? data.jobs?.find(j => j.id === bookingId) : null;
    setSelectedJob(updated || null);
    if (updated?.status === "delivered") {
      setShowPOD(true);
      toast.warning("Now upload POD photo to release your payment", {
        description: "Take a quick photo of the delivered goods to finish the job and release £" + ((updated.total_cost || 0) * 0.75).toFixed(2) + " to your Stripe account.",
        duration: 8000,
      });
    }
    setLoading(false);
  };

  const completeJob = async (bookingId) => {
    setLoading(true);
    const result = await api(`/api/driver/jobs/${bookingId}/complete`, { method: "POST" });
    if (result?.__error) {
      toast.error(result.detail || "Failed to complete job");
      setLoading(false);
      return;
    }
    if (result?.success) {
      toast.success("Job complete!");
      setSelectedJob(null);
      setShowPOD(false);
      await fetchAll();
      setTab("home");
    }
    setLoading(false);
  };

  const fetchEarnings = async () => {
    const data = await api("/api/driver/earnings");
    if (data && !data.__error) setEarnings(data);
  };

  const startStripeOnboard = async () => {
    const result = await api("/api/driver/stripe/onboard", { method: "POST" });
    if (!result) return;
    if (result.__error) {
      toast.error("Network error", { description: "Could not reach the server. Try again." });
      return;
    }
    if (result.success === false) {
      if (result.error_code === "connect_not_enabled") {
        toast.error("Stripe onboarding incomplete", {
          description: "We couldn't open onboarding right now. Please contact support so we can finish setting up your payouts.",
          duration: 8000,
        });
      } else if (result.error_code === "stripe_not_configured") {
        toast.error("Payments temporarily unavailable", {
          description: "Stripe is not configured on the platform. Contact support.",
        });
      } else {
        toast.error("Stripe setup failed", { description: result.message || "Unknown error" });
      }
      return;
    }
    if (result.already_onboarded) {
      toast.success("Stripe account already set up");
      return;
    }
    if (result.url) {
      // Open in same tab — Stripe will redirect back to /driver/dashboard?stripe=success
      window.location.assign(result.url);
    } else {
      toast.error("Stripe returned no onboarding URL");
    }
  };

  const handleProfilePictureUpload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image too large (max 8MB)");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/api/driver/me/profile-picture`, fd, {
        headers: { Authorization: `Bearer ${getToken()}` },
        validateStatus: () => true,
      });
      if (res.status < 200 || res.status >= 300) {
        toast.error(res.data?.detail || "Upload failed");
        return;
      }
      setProfile(prev => ({ ...prev, profile_picture: res.data.profile_picture }));
      toast.success("Profile picture updated");
    } catch (e) {
      toast.error("Upload failed");
    }
  };

  const handleProfilePictureRemove = async () => {
    const result = await api("/api/driver/me/profile-picture", { method: "DELETE" });
    if (result?.__error) {
      toast.error(result.detail || "Failed to remove");
      return;
    }
    setProfile(prev => ({ ...prev, profile_picture: null }));
    toast.success("Profile picture removed");
  };

  // Show toast when returning from Stripe
  useEffect(() => {
    const stripeStatus = searchParams.get("stripe");
    if (stripeStatus === "success") {
      toast.success("Stripe onboarding complete");
      setTimeout(fetchAll, 500);
    } else if (stripeStatus === "refresh") {
      toast.info("Stripe link expired — please try again");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => { localStorage.removeItem("driver_token"); navigate("/driver/login"); };

  if (!profile) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 pb-20" data-testid="driver-dashboard-v2">
      {/* Top Bar */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <button onClick={() => setTab("profile")} className="flex items-center gap-3 active:opacity-70" data-testid="header-avatar-btn">
            <Avatar src={fileUrl(profile.profile_picture)} name={profile.name} size={40} />
            <div className="text-left">
              <p className="text-sm font-semibold text-slate-900">{profile.name}</p>
              <DispatchStatusBadge status={profile.status} online={profile.online} />
            </div>
          </button>
          <div className="flex items-center gap-2">
            <PendingUploadsBadge />
            <DispatchStatusSelect
              value={profile.status || (profile.online ? "available" : "offline")}
              onChange={setDispatchStatus}
            />
            <button onClick={fetchAll} className="p-2 text-slate-400 hover:text-slate-700">
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="px-4 py-4 max-w-lg mx-auto">
        {/* HOME TAB */}
        {tab === "home" && (
          <div className="space-y-4" data-testid="tab-home">
            {/* Stats */}
            <div className="grid grid-cols-2 gap-3">
              <StatCard icon={DollarSign} label="Today" value={`\u00a3${wallet?.today_earnings?.toFixed(2) || "0.00"}`} color="text-green-600" />
              <StatCard icon={Package} label="Pending" value={`\u00a3${wallet?.pending_balance?.toFixed(2) || "0.00"}`} color="text-orange-600" />
              <StatCard icon={CheckCircle2} label="Jobs Done" value={profile.total_jobs || 0} />
              <StatCard icon={Wallet} label="Total Earned" value={`\u00a3${(profile.total_earnings || 0).toFixed(2)}`} />
            </div>

            {/* Stripe Status */}
            {profile.stripe_status !== "connected" && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="stripe-banner">
                <div className="flex items-start gap-3">
                  <CreditCard className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-amber-900 text-sm">
                      {profile.stripe_status === "incomplete" ? "Stripe onboarding incomplete" : "Set Up Payments"}
                    </p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      {profile.stripe_status === "incomplete"
                        ? "Finish your Stripe details to start receiving payouts"
                        : "Connect your Stripe account to receive payouts"}
                    </p>
                    <Button onClick={startStripeOnboard} className="mt-2 bg-amber-600 hover:bg-amber-700 text-white text-xs px-4 py-1.5 h-auto rounded-lg" data-testid="stripe-onboard-btn">
                      {profile.stripe_status === "incomplete" ? "Continue onboarding" : "Connect Stripe"}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Active Job Quick Card */}
            {activeJobs.length > 0 && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4" data-testid="active-job-quick">
                <p className="text-xs font-medium text-orange-700 mb-2">ACTIVE JOB</p>
                <JobCard job={activeJobs[0]} accepted={true} onSelect={(j) => { setSelectedJob(j); setTab("active"); }} />
              </div>
            )}

            {/* Return-loads carousel — always visible. Explains itself when not eligible. */}
            <ReturnLoadsCarousel
              driverStatus={profile.status || (profile.online ? "available" : "offline")}
              onAccept={acceptJob}
              acceptLoading={loading}
            />

            {/* Phase 11.3 — Driver Performance widget (private, opt-in, read-only) */}
            <PerformanceWidget />

            {/* Available Jobs Preview */}
            {availableJobs.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-slate-700">Available Jobs ({availableJobs.length})</p>
                  <button onClick={() => setTab("jobs")} className="text-xs text-orange-500 font-medium">View All</button>
                </div>
                <div className="space-y-2">
                  {availableJobs.slice(0, 2).map(job => (
                    <JobCard key={job.id} job={job} accepted={false} onSelect={() => setTab("jobs")} />
                  ))}
                </div>
              </div>
            )}

            {availableJobs.length === 0 && activeJobs.length === 0 && (
              <div className="text-center py-12 text-slate-400">
                <Truck className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="font-medium">No jobs available right now</p>
                <p className="text-sm mt-1">New jobs will appear here</p>
              </div>
            )}
          </div>
        )}

        {/* JOBS TAB */}
        {tab === "jobs" && (
          <div className="space-y-3" data-testid="tab-jobs">
            <h2 className="text-lg font-bold text-slate-900">Available Jobs</h2>
            {availableJobs.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Package className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No available jobs</p>
              </div>
            ) : (
              availableJobs.map(job => (
                <div key={job.id} className="bg-white rounded-xl border border-slate-200 p-4">
                  <JobCard job={job} accepted={false} onSelect={() => {}} />
                  <Button
                    onClick={() => acceptJob(job.id)}
                    disabled={loading}
                    className="w-full mt-3 bg-green-600 hover:bg-green-700 text-white font-medium py-5 rounded-lg"
                    data-testid={`accept-job-${job.id}`}
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Accept Job — Earn &pound;{(job.driver_payout || 0).toFixed(2)}
                  </Button>
                </div>
              ))
            )}
          </div>
        )}

        {/* ACTIVE TAB */}
        {tab === "active" && (
          <div className="space-y-4" data-testid="tab-active">
            <h2 className="text-lg font-bold text-slate-900">Active Jobs</h2>
            {selectedJob && !showPOD && (
              <ActiveJobView job={selectedJob} onUpdate={updateJobStatus} loading={loading} />
            )}
            {selectedJob && showPOD && (
              <PODUploadView job={selectedJob} onComplete={completeJob} loading={loading} />
            )}
            {!selectedJob && activeJobs.length > 0 && (
              <div className="space-y-3">
                {activeJobs.map(job => (
                  <JobCard key={job.id} job={job} accepted={true} onSelect={(j) => { setSelectedJob(j); setShowPOD(j.status === "delivered"); }} />
                ))}
              </div>
            )}
            {!selectedJob && activeJobs.length === 0 && (
              <div className="text-center py-12 text-slate-400">
                <Truck className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No active jobs</p>
                <button onClick={() => setTab("jobs")} className="text-orange-500 text-sm font-medium mt-2">Browse available jobs</button>
              </div>
            )}
          </div>
        )}

        {/* EARNINGS TAB */}
        {tab === "earnings" && (
          <div className="space-y-4" data-testid="tab-earnings">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">Earnings</h2>
              <Button
                onClick={downloadEarningsCSV}
                variant="outline"
                size="sm"
                className="text-xs h-8 border-slate-200"
                data-testid="export-csv-btn"
              >
                <FileText className="w-3.5 h-3.5 mr-1.5" /> Export CSV
              </Button>
            </div>
            {!earnings ? (
              <div className="text-center py-6"><Loader2 className="w-6 h-6 animate-spin text-orange-500 mx-auto" /></div>
            ) : (
              <>
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <p className="text-xs text-slate-500 mb-1">Total Earned</p>
                  <p className="text-3xl font-bold text-slate-900">&pound;{earnings.total_earned.toFixed(2)}</p>
                  <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-slate-100">
                    <div><p className="text-xs text-slate-500">Today</p><p className="font-bold text-slate-900">&pound;{earnings.today_earnings.toFixed(2)}</p></div>
                    <div><p className="text-xs text-slate-500">This Week</p><p className="font-bold text-slate-900">&pound;{earnings.week_earnings.toFixed(2)}</p></div>
                    <div><p className="text-xs text-slate-500">This Month</p><p className="font-bold text-slate-900">&pound;{earnings.month_earnings.toFixed(2)}</p></div>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-semibold text-slate-900 text-sm">Pending Payout</p>
                    <span className="text-lg font-bold text-orange-600">&pound;{earnings.pending_payout.toFixed(2)}</span>
                  </div>
                  <p className="text-xs text-slate-500">Awaiting admin approval</p>
                </div>
                {earnings.recent_payouts?.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <p className="font-semibold text-slate-900 text-sm mb-3">Recent Payouts</p>
                    <div className="space-y-2">
                      {earnings.recent_payouts.slice(0, 10).map(p => (
                        <div key={p.id} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                          <div>
                            <p className="text-sm font-medium text-slate-700">&pound;{p.driver_amount?.toFixed(2) || p.amount?.toFixed(2)}</p>
                            <p className="text-xs text-slate-400">{new Date(p.created_at).toLocaleDateString()}</p>
                          </div>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            p.status === "completed" ? "bg-green-100 text-green-700" :
                            p.status === "pending" ? "bg-amber-100 text-amber-700" :
                            "bg-red-100 text-red-700"
                          }`}>{p.status}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* PROFILE TAB */}
        {tab === "profile" && (
          <div className="space-y-4" data-testid="tab-profile">
            <h2 className="text-lg font-bold text-slate-900">Profile</h2>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              {/* Avatar with upload/remove controls */}
              <div className="flex items-center gap-4 mb-4">
                <div className="relative">
                  <Avatar src={fileUrl(profile.profile_picture)} name={profile.name} size={72} />
                  <button
                    onClick={() => document.getElementById('profile-pic-input').click()}
                    className="absolute -bottom-1 -right-1 w-7 h-7 bg-orange-500 hover:bg-orange-600 rounded-full flex items-center justify-center text-white shadow-md active:scale-90 transition-all"
                    data-testid="profile-pic-edit-btn"
                    title="Change photo"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="flex-1">
                  <p className="font-bold text-slate-900">{profile.name}</p>
                  <p className="text-sm text-slate-500 break-all">{profile.email}</p>
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => document.getElementById('profile-pic-input').click()}
                      className="text-xs text-orange-600 font-medium"
                      data-testid="profile-pic-upload-link"
                    >
                      {profile.profile_picture ? "Change photo" : "Add photo"}
                    </button>
                    {profile.profile_picture && (
                      <button
                        onClick={handleProfilePictureRemove}
                        className="text-xs text-red-500 font-medium"
                        data-testid="profile-pic-remove-btn"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <input
                id="profile-pic-input"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                capture="user"
                className="hidden"
                onChange={(e) => { handleProfilePictureUpload(e.target.files[0]); e.target.value = ""; }}
                data-testid="profile-pic-input"
              />

              <div className="space-y-3 text-sm border-t border-slate-100 pt-4">
                <div className="flex justify-between"><span className="text-slate-500">Phone</span><span className="font-medium">{profile.phone}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Vehicle</span><span className="font-medium capitalize">{profile.vehicle_type?.replace("_", " ")}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Rating</span><span className="font-medium">{profile.rating} / 5.0</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Jobs Completed</span><span className="font-medium">{profile.total_jobs}</span></div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Stripe</span>
                  <span className={`font-medium ${profile.stripe_status === "connected" ? "text-green-600" : profile.stripe_status === "incomplete" ? "text-amber-600" : "text-slate-500"}`} data-testid="profile-stripe-status">
                    {profile.stripe_status === "connected"
                      ? "Connected"
                      : profile.stripe_status === "incomplete"
                      ? "Onboarding incomplete"
                      : profile.stripe_status === "error"
                      ? "Status unavailable"
                      : "Not Connected"}
                  </span>
                </div>
              </div>
            </div>

            {profile.stripe_status !== "connected" && (
              <Button onClick={startStripeOnboard} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-5 rounded-xl" data-testid="profile-stripe-btn">
                <CreditCard className="w-5 h-5 mr-2" />
                {profile.stripe_status === "incomplete" ? "Continue Stripe Onboarding" : "Set Up Stripe Payments"}
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            )}

            <PasswordChangeDialog
              endpoint="/api/driver/me/password"
              tokenKey="driver_token"
              testId="driver-change-password-btn"
            />

            <Button onClick={logout} variant="outline" className="w-full py-5 rounded-xl border-red-200 text-red-600 hover:bg-red-50" data-testid="logout-btn">
              <LogOut className="w-5 h-5 mr-2" /> Log Out
            </Button>
          </div>
        )}
      </main>

      {/* Bottom Navigation */}
      <nav className="t247-bottomnav fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-50" data-testid="bottom-nav">
        <div className="t247-bottomnav-inner max-w-lg mx-auto flex items-center justify-around py-2">
          {[
            { id: "home", icon: Truck, label: "Home" },
            { id: "jobs", icon: Package, label: "Jobs", badge: availableJobs.length },
          ].map(item => (
            <button
              key={item.id}
              onClick={() => { setTab(item.id); setSelectedJob(null); setShowPOD(false); item.onClick?.(); }}
              className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg transition-colors relative ${
                tab === item.id ? "text-orange-500 is-active" : "text-slate-400"
              }`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-[10px] font-medium">{item.label}</span>
              {item.badge > 0 && (
                <span className="nav-badge absolute -top-0.5 right-1 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                  {item.badge}
                </span>
              )}
            </button>
          ))}
          {/* Center FAB — quick-toggle Active jobs */}
          <div className="t247-fab-wrap">
            <button
              type="button"
              className="t247-fab"
              onClick={() => { setTab("active"); setSelectedJob(null); setShowPOD(false); }}
              aria-label="Active jobs"
              data-testid="nav-active-fab"
            >
              <Navigation className="w-7 h-7" />
            </button>
            <span className="t247-fab-label">ACTIVE {activeJobs.length > 0 ? `(${activeJobs.length})` : ""}</span>
          </div>
          {[
            { id: "earnings", icon: Wallet, label: "Earnings", onClick: fetchEarnings },
            { id: "profile", icon: User, label: "Profile" },
          ].map(item => (
            <button
              key={item.id}
              onClick={() => { setTab(item.id); setSelectedJob(null); setShowPOD(false); item.onClick?.(); }}
              className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg transition-colors relative ${
                tab === item.id ? "text-orange-500 is-active" : "text-slate-400"
              }`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-[10px] font-medium">{item.label}</span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}
